import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";

import useRipple from "../../../hooks/useRipple.js";
import useInviteCodeQueries from "../../../store/InviteCodes/inviteCodeQueries";
import closeIcon from "../../../assets/images/close_icon.svg";
import "./MapInviteCodePopup.css";
import "../../../hooks/useRipple.css";

const MapInviteCodePopup = ({ isVisible, onClose, onSuccess, onError }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasMapBeenClicked, setHasMapBeenClicked] = useState(false);
  const [isSelectingTenant, setIsSelectingTenant] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const mapButtonRef = useRef(null);

  useRipple(mapButtonRef);

  const {
    isSearching,
    error,
    handleMapInviteCode,
    handleSearch,
    searchResults,
  } = useInviteCodeQueries();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setSearchQuery("");
      setSelectedTenant(null);
      setShowDropdown(false);
      setHasMapBeenClicked(false);
      setIsSelectingTenant(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      handleSearch("");
    }
  }, [isVisible]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (isSelectingTenant) {
      setIsSelectingTenant(false);
      return;
    }

    if (searchQuery.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(searchQuery).catch((error) => {
          console.error("Error searching tenants:", error);
        });
      }, 300);
    } else {
      handleSearch("");
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleTenantSelect = (tenant) => {
    setIsSelectingTenant(true);
    setSelectedTenant(tenant);
    setSearchQuery(`${tenant.tenant_name} - (${tenant.tenant_id})`);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedTenant(null);

    if (value.length < 3) {
      setShowDropdown(false);
    } else {
      setShowDropdown(true);
    }
  };

  const handleMap = async () => {
    if (selectedTenant) {
      setHasMapBeenClicked(true);
      try {
        const response = await handleMapInviteCode(
          selectedTenant.tenant_id,
          selectedTenant.tenant_name,
          false // Pass false to skip refetch
        );

        // Close popup and show notification immediately after mapping succeeds
        onSuccess(response, selectedTenant.tenant_name);
        setSearchQuery("");
        setSelectedTenant(null);
        onClose();
      } catch (error) {
        console.error("Error mapping tenant:", error);
        onError();
      }
    }
  };

  const tenants = searchResults?.tenants || [];

  // Animation variants for flexbox layout
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: prefersReducedMotion ? 0 : 0.3 },
    },
    exit: {
      opacity: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.2 },
    },
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 24,
      scale: prefersReducedMotion ? 1 : 0.96,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.35,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : -24,
      scale: prefersReducedMotion ? 1 : 0.96,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.2,
        ease: "easeIn",
      },
    },
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="map-invite-code-popup popup-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="popup-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-button-map-invite-code" onClick={onClose}>
              <img src={closeIcon} alt="close button" />
            </button>
            <div className="popup-header">
              <h3>Map New Code</h3>
            </div>

            <div className="popup-body">
              <div className="search-wrapper" ref={dropdownRef}>
                <div className="search-icon">
                  <svg
                    className="icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search anything from the table"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onClick={() =>
                    searchQuery.length >= 3 && setShowDropdown(true)
                  }
                />

                {showDropdown && searchQuery.length >= 3 && (
                  <div className="search-dropdown">
                    {isSearching ? (
                      <div className="dropdown-item loading">Loading...</div>
                    ) : error ? (
                      <div className="dropdown-item error">
                        Something went wrong
                      </div>
                    ) : tenants.length > 0 ? (
                      tenants.map((tenant) => (
                        <div
                          key={tenant.tenant_id}
                          className="dropdown-item"
                          onClick={() => handleTenantSelect(tenant)}
                        >
                          <div className="tenant-name">
                            {tenant.tenant_name} - ({tenant.tenant_id})
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item no-results">
                        No results found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {searchQuery.length > 0 && searchQuery.length < 3 && (
                <div className="search-hint">
                  Please enter at least 3 characters to search
                </div>
              )}
            </div>

            <div className="popup-footer">
              <button onClick={onClose} className="cancel-button">
                CANCEL
              </button>
              <button
                ref={mapButtonRef}
                className="map-button"
                onClick={handleMap}
                disabled={
                  isSearching ||
                  !selectedTenant ||
                  searchQuery.length < 3 ||
                  (hasMapBeenClicked &&
                    (!selectedTenant || searchQuery.length < 3))
                }
              >
                {isSearching ? "Loading..." : "MAP"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MapInviteCodePopup;
